<template>
  <div class="cl-panel-stack">
    <section class="cl-tile-strip">
      <div v-for="tile in tiles" :key="tile.label" class="cl-tile">
        <span>{{ tile.label }}</span>
        <strong>{{ tile.value }}</strong>
        <small>{{ tile.hint }}</small>
      </div>
    </section>

    <div class="cl-two-column">
      <article class="card">
        <header class="cl-card-head is-tight">
          <h3>Damage</h3>
          <span class="muted-line">{{ basisNote }}</span>
        </header>
        <CombatLogBars
          :rows="damageRows"
          :limit="12"
          clickable
          :active-key="selectedPlayerGuid"
          empty-label="No damage recorded in this fight."
          :formatter="formatNumber"
          @select="$emit('selectPlayer', $event)"
        />
      </article>

      <article class="card">
        <header class="cl-card-head is-tight">
          <h3>Healing</h3>
          <span class="muted-line">Effective healing, overhealing excluded</span>
        </header>
        <CombatLogBars
          :rows="healRows"
          :limit="12"
          clickable
          :active-key="selectedPlayerGuid"
          color="var(--cl-healing)"
          empty-label="No healing recorded in this fight."
          :formatter="formatNumber"
          @select="$emit('selectPlayer', $event)"
        />
      </article>
    </div>

    <div class="cl-two-column">
      <article class="card">
        <header class="cl-card-head is-tight">
          <h3>Damage taken</h3>
          <span class="muted-line">Absorbed amounts are reported apart from the total</span>
        </header>
        <CombatLogBars
          :rows="takenRows"
          :limit="12"
          color="var(--cl-taken)"
          empty-label="Nobody took damage in this fight."
          :formatter="formatNumber"
        />
      </article>

      <article class="card">
        <header class="cl-card-head is-tight">
          <h3>Top abilities</h3>
          <span class="muted-line">Group wide, pets merged into their owner</span>
        </header>
        <CombatLogBars :rows="report.abilities.damage" :limit="12" :formatter="formatNumber" />
      </article>
    </div>

    <details class="card cl-details-card">
      <summary>
        Log details
        <small>
          {{ summary.logVersion ? `version ${summary.logVersion}` : NO_DATA }}
          - {{ formatNumber(summary.lineCount) }} lines
          - {{ summary.unknownEvents.length }} unknown event types
        </small>
      </summary>

      <dl class="cl-definition-list">
        <div><dt>Client build</dt><dd>{{ summary.buildVersion || NO_DATA }}</dd></div>
        <div><dt>Advanced logging</dt><dd>{{ summary.advancedLogging ? 'Enabled' : 'Disabled' }}</dd></div>
        <div><dt>Zone</dt><dd>{{ summary.primaryZone ? summary.primaryZone.name : NO_DATA }}</dd></div>
        <div><dt>Events used</dt><dd>{{ formatNumber(summary.eventCount) }}</dd></div>
        <div><dt>Lines skipped</dt><dd>{{ formatNumber(summary.skippedLines) }}</dd></div>
        <div><dt>Parse errors</dt><dd>{{ formatNumber(summary.parseErrors) }}</dd></div>
        <div><dt>Recorded by</dt><dd>{{ summary.logOwner ? summary.logOwner.name : NO_DATA }}</dd></div>
        <div><dt>Group</dt><dd>{{ report.roster.length ? `${report.roster.length} from COMBATANT_INFO` : NO_DATA }}</dd></div>
      </dl>

      <ul v-if="report.roster.length" class="cl-roster">
        <li v-for="member in report.roster" :key="member.guid">
          <span class="cl-roster-name cl-class-name" :style="{ color: member.color || 'inherit' }">{{ member.name }}</span>
          <span class="cl-roster-spec">{{ member.spec || NO_DATA }} {{ member.className }}</span>
          <span class="cl-roster-role">{{ member.role || '' }}</span>
          <span class="cl-roster-ilvl">{{ member.itemLevel ? `${member.itemLevel} ilvl` : NO_DATA }}</span>
        </li>
      </ul>

      <template v-if="summary.unknownEvents.length">
        <p class="muted-line">
          These event types were counted but not interpreted, so they are missing from the statistics rather than guessed
          at:
        </p>
        <ul class="cl-chip-list">
          <li v-for="entry in summary.unknownEvents" :key="entry.name">
            {{ entry.name }} <small>{{ formatNumber(entry.count) }}</small>
          </li>
        </ul>
      </template>
    </details>
  </div>
</template>

<script>
import CombatLogBars from './CombatLogBars.vue'
import {
  NO_DATA,
  formatNumber,
  formatCompact,
  formatRate,
  formatDuration,
  formatPercent,
  describeBasis,
  basisDurationMs,
  plural,
} from '../../services/combatLog/format'

export default {
  name: 'CombatLogOverview',
  components: { CombatLogBars },
  props: {
    report: { type: Object, required: true },
    summary: { type: Object, required: true },
    basis: { type: String, default: 'segment' },
    selectedPlayerGuid: { type: String, default: '' },
  },
  emits: ['selectPlayer'],
  data() {
    return { NO_DATA }
  },
  computed: {
    basisMs() {
      return basisDurationMs(this.report, this.basis)
    },
    basisNote() {
      return `DPS ${describeBasis(this.report, this.basis)} (${formatDuration(this.basisMs)})`
    },
    damageRows() {
      return this.report.players
        .filter((player) => player.damage > 0)
        .map((player) => ({
          key: player.guid,
          name: player.name,
          amount: player.damage,
          color: player.color || 'var(--primary)',
          meta: `${formatRate(this.basis === 'active' ? player.activeDps : player.dps)} - ${formatPercent(player.damageShare)}`,
        }))
    },
    healRows() {
      return this.report.players
        .filter((player) => player.healing > 0)
        .map((player) => ({
          key: player.guid,
          name: player.name,
          amount: player.healing,
          color: player.color || 'var(--cl-healing)',
          meta: `${formatRate(this.basis === 'active' ? player.activeHps : player.hps)} - ${formatPercent(player.overhealRate)} overheal`,
        }))
    },
    takenRows() {
      return this.report.players
        .filter((player) => player.damageTaken > 0)
        .map((player) => ({
          key: `taken-${player.guid}`,
          name: player.name,
          amount: player.damageTaken,
          color: player.color || 'var(--cl-taken)',
          meta: player.damageTakenAbsorbed
            ? `${formatCompact(player.damageTakenAbsorbed)} absorbed - ${plural(player.deaths, 'death')}`
            : plural(player.deaths, 'death'),
        }))
    },
    tiles() {
      const report = this.report
      const totals = report.totals
      const basisMs = this.basisMs
      const perSecond = (amount) => (basisMs ? (amount * 1000) / basisMs : 0)

      const tiles = [
        {
          label: 'Duration',
          value: formatDuration(report.durationMs),
          hint: report.fightMs ? `fight ${formatDuration(report.fightMs)}` : `active ${formatDuration(report.activeMs)}`,
        },
        { label: 'Damage', value: formatCompact(totals.damage), hint: formatRate(perSecond(totals.damage)) },
        { label: 'Healing', value: formatCompact(totals.healing), hint: `${formatCompact(totals.overhealing)} overheal` },
        { label: 'Taken', value: formatCompact(totals.damageTaken), hint: `${formatCompact(totals.damageTakenAbsorbed)} absorbed` },
        { label: 'Deaths', value: formatNumber(totals.deaths), hint: `${formatNumber(report.enemyDeaths)} enemies` },
        { label: 'Interrupts', value: formatNumber(totals.interrupts), hint: `${formatNumber(totals.dispels)} dispels` },
        { label: 'Players', value: formatNumber(report.players.length), hint: `${formatNumber(report.eventCount)} events` },
      ]

      if (report.meta && report.meta.keystoneLevel) {
        tiles.unshift({
          label: 'Keystone',
          value: `+${report.meta.keystoneLevel}`,
          hint: report.meta.totalTimeMs ? formatDuration(report.meta.totalTimeMs) : NO_DATA,
        })
      }

      return tiles
    },
  },
  methods: {
    formatNumber,
  },
}
</script>
